// POST + SSE stream consumer.
//
// EventSource only supports GET so we use fetch streaming. The wire
// format follows the spec the middleware's streaming.py emits:
//   event: <name>\n
//   data:  <json>\n
//   \n
//
// This parser tolerates split frames across reads — every event is
// terminated by a blank line, so we accumulate into a buffer and emit
// completed frames as they appear.

export interface SSEFrame {
  event: string;
  data: unknown;
}

export interface PostStreamOptions {
  url: string;
  body: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export async function* postSSE(opts: PostStreamOptions): AsyncIterableIterator<SSEFrame> {
  const resp = await fetch(opts.url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(opts.headers ?? {}),
    },
    body: JSON.stringify(opts.body),
    signal: opts.signal,
  });
  if (!resp.ok || !resp.body) {
    const text = await resp.text().catch(() => "");
    throw new Error(`/chat returned ${resp.status}: ${text}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Frames are separated by a blank line. CRLF and LF both occur in
    // practice; normalize to LF and split on the LF+LF terminator.
    buffer = buffer.replace(/\r\n/g, "\n");
    let idx = buffer.indexOf("\n\n");
    while (idx >= 0) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const frame = parseFrame(raw);
      if (frame) yield frame;
      idx = buffer.indexOf("\n\n");
    }
  }
}

function parseFrame(raw: string): SSEFrame | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }
  if (dataLines.length === 0) return null;
  const payload = dataLines.join("\n");
  try {
    return { event, data: JSON.parse(payload) };
  } catch {
    return { event, data: payload };
  }
}
